/**
 * FLIGHT-2: Elite-Dangerous-style supercruise. A KINEMATIC high-speed travel
 * mode whose max speed is PROXIMITY-SCALED to the nearest massive body AND the
 * active nav-target distance: very fast in open space, smoothly bleeding down as
 * you near a planet or locked destination so you arrive at a sane speed.
 *
 * Positions stay Float64 in sim-core, so floating-origin precision holds even at
 * ~1e6 km/s (per 120Hz step that's ~8,000 km — fine for doubles).
 */
import {
  shouldDropIntoSignal,
  cruiseCeilingKmS,
  OPEN_SPACE_MAX,
  NEAR_BODY_MIN,
  ARRIVE_TIME,
} from '@blackmarble/sim-core';

export type CruiseState = 'NORMAL' | 'SPOOLING' | 'SUPERCRUISE';

// Re-export the pure constants so render code (supercruiseWarp.ts) can reach them
// without importing from sim-core directly (keeps the render layer's import graph lean).
export { OPEN_SPACE_MAX, NEAR_BODY_MIN, ARRIVE_TIME, cruiseCeilingKmS };

const SPOOL_TIME = 1.5; // s to engage
const ACCEL_TAU = 1.0; // s smoothing toward the target speed (no instant snaps)
// Drop prompt / surface approach band: within this multiple of body radius.
const DROP_RADII = 1.5;

export class Supercruise {
  state: CruiseState = 'NORMAL';
  private spool = 0; // 0..1 engage ramp
  private currentSpeed = 0; // km/s, smoothed
  private targetSpeed = 0;

  toggle(): void {
    if (this.state === 'NORMAL') {
      this.state = 'SPOOLING';
    } else {
      this.state = 'NORMAL';
      this.spool = 0;
      this.currentSpeed = 0;
      this.targetSpeed = 0;
    }
  }

  get active(): boolean {
    return this.state !== 'NORMAL';
  }
  get speedKmS(): number {
    return this.currentSpeed;
  }
  get spoolPercent(): number {
    return Math.round(this.spool * 100);
  }

  /**
   * Compute this frame's supercruise speed (km/s).
   * @param surfaceClearanceKm distance from ship to the NEAREST body's surface
   * @param throttle01         0..1 player throttle (latched W/S, abs value)
   * @param targetDistanceKm   distance to the active nav-target (null = no lock)
   */
  update(
    dt: number,
    surfaceClearanceKm: number | null,
    throttle01: number,
    targetDistanceKm: number | null = null,
  ): number {
    if (this.state === 'NORMAL') {
      this.currentSpeed = 0;
      return 0;
    }
    this.spool = Math.min(1, this.spool + dt / SPOOL_TIME);
    if (this.spool >= 1) this.state = 'SUPERCRUISE';

    // Target-aware ceiling: min(bodyCeiling, targetCeiling) — constant arrival time
    // for both body approach and nav-target approach.
    const ceiling = cruiseCeilingKmS(surfaceClearanceKm, targetDistanceKm);

    // Player throttle picks a fraction of the ceiling (min a little so you move).
    this.targetSpeed = ceiling * Math.max(0.05, throttle01) * this.spool;

    // Smooth toward the target (no instant snap at huge speeds).
    const k = Math.min(1, dt / ACCEL_TAU);
    this.currentSpeed += (this.targetSpeed - this.currentSpeed) * k;
    return this.currentSpeed;
  }

  /** True when within ~DROP_RADII of the body surface — show the drop prompt. */
  canDropToSurface(surfaceClearanceKm: number | null, bodyRadiusKm: number | null): boolean {
    if (surfaceClearanceKm === null || bodyRadiusKm === null) return false;
    return surfaceClearanceKm < bodyRadiusKm * (DROP_RADII - 1);
  }

  /**
   * M3 WORLD B2: signal drop-in HUD GUARD. Layers an `active` check on top of the
   * pure sim-core `shouldDropIntoSignal` band test (kept here because the HUD
   * `DROP [X]` prompt must only show while cruising). The drop-EXECUTION path in
   * main.ts calls `shouldDropIntoSignal` directly so the drop is immune to cruise
   * toggle-ordering. canDropToSurface is left untouched.
   */
  canDropToSignal(
    distKm: number | null,
    radiusKm: number | null,
    closingSpeedKmS = 0,
  ): boolean {
    return this.active && shouldDropIntoSignal(distKm, radiusKm, closingSpeedKmS);
  }

  /** Force a return to normal flight (player drop or arrival). */
  disengage(): void {
    this.state = 'NORMAL';
    this.spool = 0;
    this.currentSpeed = 0;
    this.targetSpeed = 0;
  }
}
